import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {flyoutNavigationRecipe} from '../../components/navigation/flyout/partials/flyout-navigation.recipe'
import {plainText} from './diagnostics'

/**
 * Sends a click on an authored link to a new tab, so that following one cannot
 * take the MomentVM iframe off `/momentvm`.
 *
 * In-page `#` jumps and clicks the browser already opens elsewhere — modified
 * or middle — are left alone.
 *
 * @param {MouseEvent} event - The captured click.
 */
const openAuthoredLinkInNewTab = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
    }

    const anchor = event.target?.closest?.('a[href]')
    if (!anchor) return

    // the raw attribute, so an in-page jump is recognised before the browser
    // resolves it against the current URL
    const href = anchor.getAttribute('href')
    if (!href || href.startsWith('#')) return

    event.preventDefault()
    window.open(anchor.href, '_blank', 'noopener,noreferrer')
}

/**
 * Editor chrome for one previewed pane: a dashed frame around it and a tag
 * naming the level, as a trail back up through the levels above it.
 *
 * The frame is the flyout's own `container` slot — the panel's content box,
 * where the column gap, the content insets and the tool-nav's positioning are
 * defined — so the preview cannot drift from the real thing.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] - Anchor the pane is jumped to by.
 * @param {boolean} [props.level3] - Publishes the attribute the `container`
 *   slot keys its tighter level-3 top inset on.
 * @param {Array<{label: string, anchor?: string, title?: string}>} props.trail -
 *   The levels down to this one; every entry with an `anchor` is a link back up,
 *   the last is this pane's own. A `title` names the authored category beside
 *   the level, markup stripped.
 * @param {React.ReactNode} props.children - The pane itself.
 * @returns {JSX.Element}
 */
const PreviewPane = ({id, level3 = false, trail = [], children}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Box
            id={id}
            onClickCapture={openAuthoredLinkInNewTab}
            css={{
                position: 'relative',
                // The flyout's own content width, rebuilt here: a pane is as
                // wide as the header card capped at the page width, less the
                // card's border and the panel's inline padding. The header
                // publishes those on its own root, a sibling subtree whose
                // custom properties do not inherit here, so the two constants
                // are restated from the tokens it holds them in —
                // `header/index.recipe.js`, the `root` slot. The page width
                // comes back as `100cqw` through `ModuleContainer`'s query.
                '--preview-header-offset-inline': {base: '0px', s: '{spacing.spacing-20}'},
                '--preview-frame-inline-padding': {
                    base: 'calc(var(--container-inline-padding) - var(--preview-header-offset-inline))',
                    s: '{spacing.spacing-72}'
                },
                '--preview-frame-inline-size':
                    'calc(min(100cqw + 2 * var(--container-inline-padding) - 2 * var(--preview-header-offset-inline), {sizes.container-page-max-width}) - 2 * var(--header-card-border-width, 0px))',
                // the frame is wider than this box below the cap and narrower
                // above it, so it is centred rather than pulled out
                '--preview-frame-inline-offset':
                    'calc((100% - var(--preview-frame-inline-size)) / 2)'
            }}
        >
            <Box
                css={{
                    position: 'absolute',
                    // the frame is a positioned box of its own and comes later
                    // in the tree, so it paints over the tag without this
                    zIndex: 1,
                    insetBlockStart: 0,
                    insetInlineStart: 'var(--preview-frame-inline-offset)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'spacing-4',
                    paddingInline: 'spacing-8',
                    paddingBlock: 'spacing-4',
                    bg: 'border.subtle',
                    fontSize: '10px',
                    lineHeight: 1,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                }}
            >
                {trail.map(({label, anchor, title}, idx) => (
                    <React.Fragment key={label}>
                        {idx > 0 && <Box as="span">{'\u203a'}</Box>}
                        {anchor ? (
                            <Box as="a" href={`#${anchor}`} css={{textDecoration: 'underline'}}>
                                {label}
                            </Box>
                        ) : (
                            <Box as="span">{label}</Box>
                        )}
                        {plainText(title) && (
                            <Box
                                as="span"
                                css={{
                                    // the authored title, read as content and so
                                    // set as authored rather than in the tag's
                                    // own uppercase
                                    textTransform: 'none',
                                    letterSpacing: 'normal',
                                    opacity: 0.8
                                }}
                            >
                                {plainText(title)}
                            </Box>
                        )}
                    </React.Fragment>
                ))}
            </Box>
            <Box
                data-level3={level3 || undefined}
                css={[
                    styles.container,
                    {
                        inlineSize: 'var(--preview-frame-inline-size)',
                        marginInline: 'var(--preview-frame-inline-offset)',
                        paddingInline: 'var(--preview-frame-inline-padding)',
                        // Drawn as an outline rather than a border: a border
                        // would take 4px out of the very width this box exists
                        // to match, and the editor would be reading a pane 4px
                        // narrower than the shopper's.
                        outline: '2px dashed',
                        outlineColor: 'border.subtle',
                        outlineOffset: '-2px'
                    }
                ]}
            >
                {children}
            </Box>
        </Box>
    )
}

PreviewPane.propTypes = {
    id: PropTypes.string,
    level3: PropTypes.bool,
    trail: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            anchor: PropTypes.string,
            title: PropTypes.string
        })
    ),
    children: PropTypes.node
}

export default PreviewPane
